/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { TeamMembershipRolePermissions } from '@fai/friendli-suite/features/common/team-membership-role';
import {
  ClientTeamMembershipRole,
  ContainerSubplanTier,
  ContainerSubplanState,
} from '@fai/friendli-suite/generated/client/graphql';

interface ContainerPlanRawData {
  teamRole?: ClientTeamMembershipRole | null;
  isProductAccessible?: boolean | null;
  productPlan?: ContainerSubplanTier | null;
  productState?: ContainerSubplanState | null;
}

export type ContainerProductPlan =
  | 'ACCESS_REQUIRED'
  | 'ENTERPRISE'
  | 'NONE_MEMBER'
  | 'NONE_OWNER'
  | 'TRIAL_EXPIRED'
  | 'TRIAL';

export const convertRawDataToPlan = ({
  teamRole,
  isProductAccessible,
  productPlan,
  productState,
}: ContainerPlanRawData): ContainerProductPlan => {
  const { isTeamOwner } = TeamMembershipRolePermissions(teamRole);

  let planResult: ContainerProductPlan = isTeamOwner ? 'NONE_OWNER' : 'NONE_MEMBER';

  // trial
  if (productPlan === ContainerSubplanTier.Trial) {
    if (productState === ContainerSubplanState.Approved) {
      // trial approved case. TRIAL except user is member and inaccessible.
      planResult = isTeamOwner || isProductAccessible ? 'TRIAL' : 'ACCESS_REQUIRED';
    }

    // trial is expired
    if (productState === ContainerSubplanState.Expired) {
      // trial expired case. TRIAL_EXPIRED for owner, but it's shown as NONE for member.
      planResult = isTeamOwner ? 'TRIAL_EXPIRED' : 'NONE_MEMBER';
    }
  }

  // enterprise
  if (productPlan === ContainerSubplanTier.Enterprise) {
    // ENTERPRISE except user is member and inaccessible.
    planResult = isTeamOwner || isProductAccessible ? 'ENTERPRISE' : 'ACCESS_REQUIRED';
  }

  // TODO: error control for UNKNOWN state

  return planResult;
};

export const getIsPlanInaccessible = (plan: ContainerProductPlan) =>
  ['NONE_OWNER', 'NONE_MEMBER', 'TRIAL_EXPIRED', 'ACCESS_REQUIRED'].includes(plan);
